// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'already' }
  | { kind: 'ready'; data: any }
  | { kind: 'done'; action: 'accepted' | 'declined' };

function KeyValueList({ params }: { params: Record<string, any> | null | undefined }) {
  if (!params || typeof params !== 'object' || Object.keys(params).length === 0) return null;
  return (
    <dl className="space-y-1 text-sm">
      {Object.entries(params).map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <dt className="font-medium text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</dt>
          <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function SchedulingResponsePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await invokeEdgeFunction('respond-to-renegotiation', {
        body: { token, action: 'get' },
      });
      if (cancelled) return;
      if (error || !data?.ok) {
        setState({ kind: 'invalid' });
        return;
      }
      if (data.already_resolved) {
        setState({ kind: 'already' });
        return;
      }
      setState({ kind: 'ready', data });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const respond = async (action: 'accept' | 'decline') => {
    setSubmitting(true);
    setErrorMsg(null);
    const { data, error } = await invokeEdgeFunction('respond-to-renegotiation', {
      body: { token, action },
    });
    setSubmitting(false);
    if (error || !data?.ok) {
      setErrorMsg(error?.message || data?.error || 'Something went wrong. Please try again.');
      return;
    }
    setState({ kind: 'done', action: action === 'accept' ? 'accepted' : 'declined' });
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {state.kind === 'loading' && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {state.kind === 'invalid' && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              This link is invalid or has expired.
            </CardContent>
          </Card>
        )}

        {state.kind === 'already' && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              You've already responded to this proposal.
            </CardContent>
          </Card>
        )}

        {state.kind === 'done' && (
          <Card>
            <CardContent className="py-12 text-center">
              {state.action === 'accepted'
                ? 'Thanks — your scheduling request is confirmed with the proposed change.'
                : 'Understood. Your request has been withdrawn and any fee will be refunded.'}
            </CardContent>
          </Card>
        )}

        {state.kind === 'ready' && (
          <Card>
            <CardHeader>
              <CardTitle>A scheduling proposal for {state.data.team_name}</CardTitle>
              <p className="text-sm text-muted-foreground">{state.data.event_name}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <section>
                <h3 className="font-semibold mb-2">Your original request</h3>
                <p className="text-sm text-muted-foreground mb-1 capitalize">
                  {String(state.data.constraint_type || '').replace(/_/g, ' ')}
                </p>
                <KeyValueList params={state.data.original_params} />
              </section>

              <section>
                <h3 className="font-semibold mb-2">The tournament director proposes</h3>
                {state.data.renegotiation_message && (
                  <p className="text-sm whitespace-pre-wrap mb-2">
                    {state.data.renegotiation_message}
                  </p>
                )}
                <KeyValueList params={state.data.proposed_params} />
              </section>

              {errorMsg && (
                <div className="rounded-md bg-destructive/10 text-destructive text-sm p-3">
                  {errorMsg}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  className="flex-1"
                  disabled={submitting}
                  onClick={() => respond('accept')}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept this alternative'}
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  disabled={submitting}
                  onClick={() => respond('decline')}
                >
                  Decline
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                If you decline, any fee paid for this request will be refunded.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
